import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Alert, Card, Col, Descriptions, Radio, Row, Segmented, Spin, Statistic, Table, Tag, Typography,
} from "antd";
import actions from "../../redux/admin/actions";
import { adminDateDaysAgo, formatAdminDate, formatAdminDateTime } from "../../dates";
import Wrapper from "./index.style";

const PERIODS = { today: 0, sevenDays: 6, thirtyDays: 29 };
const { fetchIntelligenceCosts } = actions;

export class IntelligenceCostsPanel extends Component {
  state = { period: "sevenDays", groupBy: "day" };

  componentDidMount() {
    this.fetchReport();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.period !== this.state.period || prevState.groupBy !== this.state.groupBy) {
      this.fetchReport();
    }
  }

  fetchReport = () => {
    const { period, groupBy } = this.state;
    this.props.fetchIntelligenceCosts({
      from: adminDateDaysAgo(PERIODS[period]),
      to: adminDateDaysAgo(0),
      group_by: groupBy,
    });
  };

  render() {
    const { t, i18n, report, loading, error } = this.props;
    const { period, groupBy } = this.state;
    if (error) return <Alert type="error" message={error} />;
    if (loading || !report) return <Spin />;

    const { summary, breakdown, alerts, configuration } = report;
    const usd = (value) => value === null || value === undefined
      ? t("admin.intelligence.unknown")
      : new Intl.NumberFormat(i18n.language, {
        style: "currency", currency: "USD", minimumFractionDigits: 4,
      }).format(Number(value));
    const stats = [
      [t("admin.intelligence.answeredQueries"), summary.answered_queries],
      [t("admin.intelligence.modelCalls"), summary.model_calls],
      [t("admin.intelligence.repairRate"), `${(summary.repair_rate * 100).toFixed(1)}%`],
      [t("admin.intelligence.inputTokens"), `${(summary.input_tokens / 1e6).toFixed(2)}M`],
      [t("admin.intelligence.outputTokens"), `${(summary.output_tokens / 1e6).toFixed(2)}M`],
      [t("admin.intelligence.reasoningTokens"), `${(summary.reasoning_tokens / 1e6).toFixed(2)}M`],
      [t("admin.intelligence.estimatedAiCost"), usd(summary.estimated_cost_usd)],
      [t("admin.intelligence.costPerQuery"), usd(summary.cost_per_answered_query_usd)],
      [t("admin.intelligence.p50Latency"), summary.p50_model_latency_ms ? t("admin.intelligence.milliseconds", { value: summary.p50_model_latency_ms }) : t("admin.common.notAvailable")],
      [t("admin.intelligence.p95Latency"), summary.p95_model_latency_ms ? t("admin.intelligence.milliseconds", { value: summary.p95_model_latency_ms }) : t("admin.common.notAvailable")],
      [t("admin.intelligence.failedCalls"), summary.failed_calls],
      [t("admin.intelligence.unpricedCalls"), summary.unpriced_calls],
    ];
    const columns = [
      {
        title: t(`admin.intelligence.${breakdown.group_by}`),
        dataIndex: "label",
        key: "label",
        render: (value) => breakdown.group_by === "day" ? formatAdminDate(value, i18n.language) : value,
      },
      { title: t("admin.intelligence.modelCalls"), dataIndex: "model_calls", key: "model_calls" },
      { title: t("admin.intelligence.inputTokens"), dataIndex: "input_tokens", key: "input_tokens" },
      { title: t("admin.intelligence.outputTokens"), dataIndex: "output_tokens", key: "output_tokens" },
      {
        title: t("admin.intelligence.estimatedAiCost"), dataIndex: "estimated_cost_usd", key: "cost", render: usd,
      },
    ];

    return (
      <Wrapper>
        <Row gutter={[16, 16]} align="middle" className="heading-row">
          <Col flex="auto">
            <Typography.Title level={4} className="page-title">{t("admin.intelligence.title")}</Typography.Title>
            <Typography.Text type="secondary">
              {t("admin.intelligence.subtitle")} · {report.pricing_version}
            </Typography.Text>
          </Col>
          <Col>
            <Segmented
              options={Object.keys(PERIODS).map((value) => ({ value, label: t(`admin.intelligence.periods.${value}`) }))}
              value={period}
              onChange={(value) => this.setState({ period: value })}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {stats.map(([title, value]) => (
            <Col xs={12} md={8} lg={4} key={title}>
              <Card size="small"><Statistic title={title} value={value ?? "—"} /></Card>
            </Col>
          ))}
        </Row>

        {alerts.length > 0 && (
          <Card size="small" title={t("admin.intelligence.costAlerts")} className="section-card">
            {alerts.map((alert, index) => (
              <div key={index} className="alert-row">
                <Tag color={alert.event_type === "cost_critical" ? "red" : "orange"}>{alert.event_type}</Tag>
                {alert.reason_code} · {t("admin.intelligence.organizationShort", {
                  id: alert.organization_id ?? t("admin.common.notAvailable"),
                })} · {JSON.stringify(alert.metadata)} · {formatAdminDateTime(
                  alert.created_at, alert.organization_timezone, i18n.language,
                )}
              </div>
            ))}
          </Card>
        )}

        <Card
          size="small"
          className="section-card"
          title={t("admin.intelligence.breakdown")}
          extra={(
            <Radio.Group
              size="small"
              value={groupBy}
              onChange={(event) => this.setState({ groupBy: event.target.value })}
              options={["day", "organization", "model", "role"].map((value) => ({
                value, label: t(`admin.intelligence.${value}`),
              }))}
              optionType="button"
            />
          )}
        >
          <Table size="small" rowKey="key" columns={columns} dataSource={breakdown.rows} pagination={false} />
        </Card>

        <Card size="small" title={t("admin.intelligence.effectiveConfiguration")} className="section-card">
          <Descriptions size="small" column={2}>
            {Object.entries(configuration).map(([key, value]) => (
              <Descriptions.Item key={key} label={t(`admin.intelligence.configuration.${key}`)}>
                {String(value)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      </Wrapper>
    );
  }
}

IntelligenceCostsPanel.propTypes = {
  report: PropTypes.shape({
    pricing_version: PropTypes.string.isRequired,
    summary: PropTypes.shape({
      answered_queries: PropTypes.number.isRequired,
      model_calls: PropTypes.number.isRequired,
      repair_rate: PropTypes.number.isRequired,
      input_tokens: PropTypes.number.isRequired,
      output_tokens: PropTypes.number.isRequired,
      reasoning_tokens: PropTypes.number.isRequired,
      estimated_cost_usd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cost_per_answered_query_usd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      p50_model_latency_ms: PropTypes.number,
      p95_model_latency_ms: PropTypes.number,
      failed_calls: PropTypes.number.isRequired,
      unpriced_calls: PropTypes.number.isRequired,
    }).isRequired,
    breakdown: PropTypes.shape({
      group_by: PropTypes.string.isRequired,
      rows: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        model_calls: PropTypes.number.isRequired,
        input_tokens: PropTypes.number.isRequired,
        output_tokens: PropTypes.number.isRequired,
        estimated_cost_usd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      })).isRequired,
    }).isRequired,
    alerts: PropTypes.arrayOf(PropTypes.shape({
      event_type: PropTypes.string.isRequired,
      reason_code: PropTypes.string.isRequired,
      organization_id: PropTypes.number,
      organization_timezone: PropTypes.string,
      metadata: PropTypes.object.isRequired,
      created_at: PropTypes.string.isRequired,
    })).isRequired,
    configuration: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.string, PropTypes.number, PropTypes.bool,
    ])).isRequired,
  }),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  fetchIntelligenceCosts: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string.isRequired }).isRequired,
};

IntelligenceCostsPanel.defaultProps = {
  report: null,
  error: null,
};

const mapStateToProps = ({ Admin }) => ({
  report: Admin.intelligenceReport,
  loading: Admin.intelligenceLoading,
  error: Admin.intelligenceError,
});

const mapDispatchToProps = (dispatch) => ({
  fetchIntelligenceCosts: (params) => dispatch(fetchIntelligenceCosts(params)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("common")(IntelligenceCostsPanel));
